import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { Match, Number } from 'effect'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHarmonicIntervalFn } from 'react-use'

import { buildHeaderHeight, type RSheetColumn, type RSheetRecordNode } from '../../domain/column'
import type { RSheetsCursorPosition } from '../../domain/cursor/cursorTypes'
import type { RSheetContext } from '../../recoil/context'
import { useCursorMutation, useGetInnerCells } from '../../recoil/cursor'
import { useCalculatedRowHeight } from '../../recoil/navigation/hooks'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'

const DRAG_TRIGGER_BORDER_PX = 50
const DRAG_INTERVAL_TIME = 150

export const useCellRangeDrag = ({
  context,
  rows,
  columns,
  compiledConfig,
  ability,
  visibleRowNumber,
}: {
  context: RSheetContext
  rows: ViewQueryRecordNodeWithId[]
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  ability: ViewAbility & SalescoreObjectAbility
  visibleRowNumber: number | undefined
}) => {
  const isDraggingReference = useRef(false)

  const { expand, expandWithDiff } = useCursorMutation(ability)
  const { headerHeight } = useMemo(() => buildHeaderHeight(columns, context), [columns, context])
  const calculatedRowHeight = useCalculatedRowHeight(compiledConfig)
  const [moveDiff, setMoveDiff] = useState<{ dx: 1 | -1 | 0; dy: 1 | -1 | 0 }>({ dx: 0, dy: 0 })
  const intervalTime = moveDiff.dx === 0 && moveDiff.dy === 0 ? null : DRAG_INTERVAL_TIME
  const getInnerCells = useGetInnerCells(visibleRowNumber)

  useEffect(() => {
    const stopDragging = (): void => {
      isDraggingReference.current = false
      setMoveDiff({ dx: 0, dy: 0 })
    }

    globalThis.addEventListener('pointerup', stopDragging)
    // Windowsだと画面遷移時にpointerupが発火しないため、visibilitychangeでもドラッグを停止させる
    globalThis.addEventListener('visibilitychange', stopDragging)

    return (): void => {
      globalThis.removeEventListener('pointerup', stopDragging)
      globalThis.removeEventListener('visibilitychange', stopDragging)
    }
  }, [])

  useHarmonicIntervalFn(() => {
    expandWithDiff(moveDiff, rows, columns, getInnerCells)
  }, intervalTime)

  return {
    expandOnDrag: useCallback(
      (position: RSheetsCursorPosition): void => {
        if (isDraggingReference.current) expand(position)
      },
      [expand],
    ),
    setDragging: useCallback((): void => {
      isDraggingReference.current = true
    }, []),
    bookDragExpand: useCallback(
      (event: React.MouseEvent<HTMLDivElement>, rootDivElement: HTMLDivElement | null) => {
        if (rootDivElement === null || !isDraggingReference.current) return

        const areaHeight = rootDivElement.clientHeight
        const areaWidth = rootDivElement.clientWidth

        const pointerPosition = {
          x: event.clientX - rootDivElement.getBoundingClientRect().left,
          y: event.clientY - rootDivElement.getBoundingClientRect().top,
        }
        const border = {
          left: DRAG_TRIGGER_BORDER_PX,
          right: areaWidth - DRAG_TRIGGER_BORDER_PX,
          top: headerHeight,
          bottom: areaHeight - calculatedRowHeight,
        }
        const moveDiff = calcMoveDiff(pointerPosition, border)
        setMoveDiff((previous) => (isSameDiff(previous, moveDiff) ? previous : moveDiff))
      },
      [calculatedRowHeight, headerHeight],
    ),
  }
}

const isSameDiff = (a: { dx: number; dy: number }, b: { dx: number; dy: number }): boolean =>
  a.dx === b.dx && a.dy === b.dy
export const calcMoveDiff = (
  pointerPosition: Record<'x' | 'y', number>,
  border: Record<'left' | 'right' | 'top' | 'bottom', number>,
) => ({
  dx: Match.value(pointerPosition.x).pipe(
    Match.when(Number.isLessThan(border.left), () => -1 as const),
    Match.when(Number.isGreaterThan(border.right), () => 1 as const),
    Match.orElse(() => 0 as const),
  ),
  dy: Match.value(pointerPosition.y).pipe(
    Match.when(Number.isLessThan(border.top), () => -1 as const),
    Match.when(Number.isGreaterThan(border.bottom), () => 1 as const),
    Match.orElse(() => 0 as const),
  ),
})
